import { useEffect, useMemo, useState, useSyncExternalStore } from "react";
import { TrashIcon } from "@heroicons/react/24/outline";
import toast from "react-hot-toast";
import { isPrebuiltWatchlist } from "../prebuiltWatchlists";
import {
  createdListKey,
  forgetCreatedLists,
  getRecentlyCreatedSnapshot,
  subscribeToRecentlyCreated,
} from "../recentlyCreated";
import { useDeleteWatchlists } from "../api/useDeleteWatchlists";
import { usePortfolioBuilderOptional } from "./PortfolioBuilderContext";
import {
  ListPanel,
  ListRow,
  SelectRow,
  GroupLabel,
  EmptyNote,
} from "./ListParts";
import {
  portfolioEntry,
  tagWatchlistEntry,
  combinedEntry,
  byName,
  matches,
  type ListEntry,
} from "../listEntries";
import { useTagsWatchlistsCRUD } from "@/shared/api/pref-storage/tags-watchlists/useTagsWatchlistsCRUD";
import { useHeadlineQueriesCRUD } from "@/widget-components/newsquawk-headlines/api/headline-query/useHeadlineQueriesCRUD";
import { useConfirm } from "@/ui-components/ConfirmDialog";
import { formatListName } from "@/shared/utils/formatListName";
import type { Portfolio } from "@/types/Portfolio";
import type { CategoryQuery } from "@/widget-components/newsquawk-headlines/models/CategoryQuery";
import type { EditableWatchlist } from "../hooks/useWatchlistCreator";

/** Enough to find what you just made; more and it becomes a second list. */
const RECENT_LIMIT = 5;
const RECENT_WINDOW_MS = 7 * 24 * 60 * 60 * 1000;

const quoted = (entries: ListEntry[], max = 3): string => {
  const names = entries.map((e) => `“${formatListName(e.name)}”`);
  if (names.length === 1) return names[0];
  if (names.length <= max) {
    return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
  }
  const others = names.length - max;
  return `${names.slice(0, max).join(", ")} and ${others} other${others === 1 ? "" : "s"}`;
};

function formatAgo(at: number, now: number): string {
  const minutes = Math.floor((now - at) / 60_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return days === 1 ? "Yesterday" : `${days}d ago`;
}

/** Re-renders on an interval, so "Just now" doesn't outlive the moment. */
function useNow(intervalMs: number, active: boolean): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!active) return;
    setNow(Date.now());
    const id = window.setInterval(() => setNow(Date.now()), intervalMs);
    return () => window.clearInterval(id);
  }, [intervalMs, active]);
  return now;
}

interface YourWatchlistsProps {
  portfolios: Portfolio[];
  onOpen: (item: EditableWatchlist) => void;
  onInspect: (item: EditableWatchlist) => void;
  /**
   * Lists created at or after this moment are the ones "you just made" and
   * are highlighted. Null highlights nothing — on arrival, nothing on the
   * page is new to the person looking at it.
   */
  highlightSince: number | null;
}

/**
 * Every list the user owns, under the two ways of starting a new one.
 *
 * It sat in a narrow rail beside the builder, which made it reference
 * material for a form that is not on screen once you have saved — so after a
 * save you landed on two buttons and a column you had to scan for the list
 * you had just made. With "Recently created" first, the answer to "did that
 * work?" is the first thing on the page.
 *
 * Also where the housekeeping happens: selecting several lists and deleting
 * them together, which previously meant opening each one in the editor.
 */
export default function YourWatchlists({
  portfolios,
  onOpen,
  onInspect,
  highlightSince,
}: YourWatchlistsProps) {
  // Both already mounted by the page; this is a different view of cached data.
  const { watchlistsQuery } = useTagsWatchlistsCRUD();
  const { queriesQuery } = useHeadlineQueriesCRUD();
  const tagWatchlists = watchlistsQuery.data;
  const combinedWatchlists = queriesQuery.data;

  /**
   * Every list the user owns, whatever store it lives in. Kind is carried
   * only to route a click to the right editor; the row says "watchlist" like
   * all the others, except for the Combined chip.
   */
  const rows = useMemo(() => {
    const out: ListEntry[] = [];
    for (const p of portfolios) {
      if (!isPrebuiltWatchlist(p)) out.push(portfolioEntry(p));
    }
    for (const w of tagWatchlists ?? []) {
      if (w.owner_type === "USER") out.push(tagWatchlistEntry(w));
    }
    for (const q of combinedWatchlists ?? []) {
      if (q.owner_type === "USER" && q.id !== undefined) {
        out.push(combinedEntry({ ...q, id: q.id }));
      }
    }
    return out.sort(byName);
  }, [portfolios, tagWatchlists, combinedWatchlists]);

  const created = useSyncExternalStore(
    subscribeToRecentlyCreated,
    getRecentlyCreatedSnapshot,
    getRecentlyCreatedSnapshot
  );
  const now = useNow(60_000, created.length > 0);

  // A create is noted when the request returns, the row arrives with the
  // refetch after it — so an entry can briefly have no row, and simply
  // appears (flare and all) once it does.
  const recent = useMemo(() => {
    const byKey = new Map(rows.map((r) => [r.key, r]));
    const out: { row: ListEntry; at: number }[] = [];
    for (const entry of created) {
      if (now - entry.at > RECENT_WINDOW_MS) continue;
      const row = byKey.get(entry.key);
      if (row) out.push({ row, at: entry.at });
      if (out.length === RECENT_LIMIT) break;
    }
    return out;
  }, [rows, created, now]);

  const [search, setSearch] = useState("");
  const query = search.trim().toLowerCase();
  const visible = rows.filter((r) => matches(r, query));

  // ── Selection ────────────────────────────────────────────────────────
  const [selecting, setSelecting] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  // Read through the rows so a list that disappears (deleted elsewhere, or by
  // the refetch after a delete here) stops counting as selected.
  const selectedRows = rows.filter((r) => selected.has(r.key));
  const allVisibleSelected =
    visible.length > 0 && visible.every((r) => selected.has(r.key));

  const toggle = (key: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });

  // Select all acts on what the search shows, so "search 'test', select all,
  // delete" clears out a batch of scratch lists in three moves.
  const toggleAllVisible = () =>
    setSelected((prev) => {
      const next = new Set(prev);
      for (const r of visible) {
        if (allVisibleSelected) next.delete(r.key);
        else next.add(r.key);
      }
      return next;
    });

  const stopSelecting = () => {
    setSelecting(false);
    setSelected(new Set());
  };

  // ── Delete ───────────────────────────────────────────────────────────
  const confirm = useConfirm();
  const deleteLists = useDeleteWatchlists();
  const builder = usePortfolioBuilderOptional();

  const handleDelete = async () => {
    const targets = selectedRows;
    if (targets.length === 0 || deleteLists.isPending) return;

    /**
     * Combined watchlists that are staying but are built from lists that are
     * going. Worth saying before rather than discovering after — the
     * combination is still there and still named, but quietly matches less.
     */
    const going = new Set(targets.map((r) => r.key));
    const affected = rows.filter((r) => {
      if (r.item.kind !== "combined" || going.has(r.key)) return false;
      const q: CategoryQuery = r.item.query;
      return (
        (q.portfolioIds ?? []).some((id) =>
          going.has(createdListKey("watchlist", id))
        ) ||
        (q.tagsWatchlistIds ?? []).some((id) =>
          going.has(createdListKey("tag", id))
        )
      );
    });

    const count = targets.length;
    const ok = await confirm({
      title: count === 1 ? "Delete watchlist" : `Delete ${count} watchlists`,
      message:
        `${quoted(targets)} will be permanently deleted. This can't be undone.` +
        (affected.length > 0
          ? ` ${quoted(affected)} ${affected.length === 1 ? "is" : "are"} built from ${count === 1 ? "it" : "some of these"} and will stop matching ${count === 1 ? "it" : "them"}.`
          : ""),
      confirmLabel: count === 1 ? "Delete" : `Delete ${count}`,
      variant: "danger",
    });
    if (!ok) return;

    const { deleted, failed } = await deleteLists.mutateAsync(
      targets.map((r) => r.item)
    );
    // The hook hands back the same item objects it was given.
    const keyOf = (item: EditableWatchlist) =>
      targets.find((t) => t.item === item)?.key ?? "";
    const deletedKeys = new Set(deleted.map(keyOf));
    forgetCreatedLists([...deletedKeys]);

    // Don't leave the headline strip previewing something that is gone.
    const preview = builder?.previewState;
    if (preview?.id != null) {
      const kind =
        preview.type === "watchlist"
          ? "watchlist"
          : preview.type === "tagWatchlist"
            ? "tag"
            : "combined";
      if (deletedKeys.has(createdListKey(kind, preview.id))) {
        builder?.clearPreview();
      }
    }

    if (failed.length === 0) {
      toast.success(
        deleted.length === 1
          ? "Watchlist deleted"
          : `${deleted.length} watchlists deleted`
      );
      stopSelecting();
    } else {
      // Keep the survivors selected, so trying again is one press.
      const failedKeys = new Set(failed.map(keyOf));
      toast.error(
        `Couldn't delete ${quoted(targets.filter((t) => failedKeys.has(t.key)))}`
      );
      setSelected(failedKeys);
    }
  };

  // In selection mode the list is shown once: a recent list appearing in
  // both groups would tick twice, and "Select all" would be ambiguous.
  const showRecent = !selecting && !query && recent.length > 0;
  const deleting = deleteLists.isPending;

  return (
    <ListPanel
      title="Your watchlists"
      count={rows.length}
      search={
        rows.length > 0
          ? {
              value: search,
              onChange: setSearch,
              placeholder: "Search your watchlists...",
            }
          : undefined
      }
      actions={
        !selecting &&
        rows.length > 0 && (
          <button
            type="button"
            onClick={() => setSelecting(true)}
            className="cursor-pointer rounded border-0 px-1.5 py-0.5 text-xs text-gray-400 transition-colors hover:bg-gray-800 hover:text-gray-100"
          >
            Select
          </button>
        )
      }
      footer={
        // Like the menus' Done footer: the controls for what you have ticked,
        // always in reach at the bottom however long the list.
        selecting && (
          <div className="flex shrink-0 items-center justify-between gap-2 border-t border-gray-700/30 bg-gray-800 px-3 py-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={toggleAllVisible}
                disabled={visible.length === 0}
                className="cursor-pointer border-0 bg-transparent p-0 text-xs text-gray-400 transition-colors hover:text-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                {allVisibleSelected
                  ? "Clear"
                  : query
                    ? `Select ${visible.length} shown`
                    : "Select all"}
              </button>
              <span className="text-xs tabular-nums text-gray-500">
                {selectedRows.length} selected
              </span>
            </div>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={stopSelecting}
                disabled={deleting}
                className="cursor-pointer rounded border border-gray-600 bg-gray-700/50 px-3 py-1 text-xs font-medium text-gray-300 transition-colors hover:bg-gray-700 hover:text-gray-100 disabled:cursor-not-allowed disabled:opacity-40"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                disabled={selectedRows.length === 0 || deleting}
                data-analytics-id="watchlists-bulk-delete"
                className="flex cursor-pointer items-center gap-1.5 rounded border border-red-800/60 px-3 py-1 text-xs font-medium text-red-400 transition-colors enabled:hover:bg-red-900/20 enabled:hover:text-red-300 disabled:cursor-not-allowed disabled:opacity-40"
              >
                <TrashIcon className="size-3.5" />
                {deleting
                  ? "Deleting…"
                  : selectedRows.length > 0
                    ? `Delete ${selectedRows.length}`
                    : "Delete"}
              </button>
            </div>
          </div>
        )
      }
    >
      {rows.length === 0 ? (
        <EmptyNote>Nothing here yet — your first one will appear here.</EmptyNote>
      ) : visible.length === 0 ? (
        <EmptyNote>No watchlists match &quot;{search.trim()}&quot;</EmptyNote>
      ) : (
        <div className="space-y-1.5">
          {showRecent && (
            <div>
              <GroupLabel>Recently created</GroupLabel>
              {recent.map(({ row, at }) => (
                <ListRow
                  key={row.key}
                  entry={row}
                  action="Edit"
                  fresh={highlightSince !== null && at >= highlightSince}
                  ago={formatAgo(at, now)}
                  onOpen={() => onOpen(row.item)}
                  onInspect={() => onInspect(row.item)}
                />
              ))}
            </div>
          )}
          <div>
            {showRecent && <GroupLabel>All</GroupLabel>}
            {visible.map((row) =>
              selecting ? (
                <SelectRow
                  key={row.key}
                  entry={row}
                  checked={selected.has(row.key)}
                  onToggle={() => toggle(row.key)}
                />
              ) : (
                <ListRow
                  key={row.key}
                  entry={row}
                  action="Edit"
                  onOpen={() => onOpen(row.item)}
                  onInspect={() => onInspect(row.item)}
                />
              )
            )}
          </div>
        </div>
      )}
    </ListPanel>
  );
}
